import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import LoadDetailsModal from './LoadDetailsModal';
import { useTranslation } from '../../../hooks/useTranslation';
import BuySugarModal from './BuySugarModal';
import MoreDetailsModal from './MoreDetailsModal';
import SugarTruckMultiModal from './SugarTruckMultiModal';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const MarketplaceCards = ({ loads, onBookSugarOnly, onBookBundle, highlightedLoadId }) => {
  const { t } = useTranslation();
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isBuySugarModalOpen, setIsBuySugarModalOpen] = useState(false);
  const [isMoreDetailsModalOpen, setIsMoreDetailsModalOpen] = useState(false);
  const [sugarTruckModal, setSugarTruckModal] = useState(null);
  // For Book Sugar+Truck flow: track which load is in quantity-selection step
  const [sugarTruckQuantityLoad, setSugarTruckQuantityLoad] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  // Mandi-selected offer: brief pulse on arrival, then settles back to normal —
  // the "Selected from SugarHathi Mandi" badge (driven directly off
  // highlightedLoadId, not this) is what stays visible after the pulse fades.
  const [pulsingLoadId, setPulsingLoadId] = useState(null);
  const pulseTimeoutRef = useRef(null);

  useEffect(() => {
    if (!highlightedLoadId) return;
    // Hardened against a long/image-heavy list not having finished its layout pass
    // yet at the exact instant this effect fires — requestAnimationFrame waits for
    // the next paint, then a couple of short retries cover a card that's still
    // mounting (e.g. images loading) on slower connections/devices.
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(`marketplace-offer-${highlightedLoadId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (attempts < 5) {
        attempts += 1;
        setTimeout(() => requestAnimationFrame(tryScroll), 150);
      }
    };
    requestAnimationFrame(tryScroll);
    setPulsingLoadId(highlightedLoadId);
    pulseTimeoutRef.current = setTimeout(() => setPulsingLoadId(null), 2500);
    return () => clearTimeout(pulseTimeoutRef.current);
  }, [highlightedLoadId]);

  const handleEnquire = (load) => { setSelectedLoad(load); setIsMoreDetailsModalOpen(true); };
  const handleBuySugar = (load) => {
    if (!user || !user?.id) { setShowLoginModal(true); return; }
    setSelectedLoad(load); setIsBuySugarModalOpen(true);
  };

  // Book Sugar + Truck: Step 1 — open quantity modal
  const handleBookSugarTruck = (load) => {
    if (!user || !user?.id) { setShowLoginModal(true); return; }
    setSugarTruckQuantityLoad(load);
  };

  // Book Sugar + Truck: Step 2 — quantity confirmed, open truck modal
  const handleSugarTruckQuantityConfirm = (purchaseData) => {
    const qty = purchaseData?.selectedQuantity || purchaseData?.quantity;
    setSugarTruckModal({ ...sugarTruckQuantityLoad, selectedQuantity: qty });
    setSugarTruckQuantityLoad(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })?.format(amount);
  };

  const gradeColor = (grade) => {
    const map = {
      'S-30': 'bg-blue-100 text-blue-700 border-blue-300',
      'S-31': 'bg-indigo-100 text-indigo-700 border-indigo-300',
      'M-30': 'bg-purple-100 text-purple-700 border-purple-300',
      'L-30': 'bg-teal-100 text-teal-700 border-teal-300'
    };
    return map?.[grade] || 'bg-slate-100 text-slate-600 border-slate-300';
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {loads?.map((load, index) => (
          <div
            key={load?.id}
            id={`marketplace-offer-${load?.id}`}
            className="rounded-2xl overflow-hidden border transition-all duration-200 hover:-translate-y-1"
            style={{
              background: index % 2 === 0 ? '#ffffff' : '#faf7f2',
              borderColor: 'rgba(0,0,0,0.1)',
              boxShadow: pulsingLoadId === load?.id ? '0 0 0 3px rgba(217,119,6,0.5), 0 2px 12px rgba(0,0,0,0.08)' : '0 2px 12px rgba(0,0,0,0.08)',
              transition: 'box-shadow 0.6s ease'
            }}
          >
            {/* Card Header */}
            <div className="px-5 pt-5 pb-4 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold border ${gradeColor(load?.grade)}`}>
                      {load?.grade}
                    </span>
                    <span className="text-sm font-semibold truncate" style={{ color: '#1e293b' }}>White Refined Sugar</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    <span className="text-xs font-medium" style={{ color: '#9ca3af' }}>Offer Ref:</span>
                    <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ background: 'rgba(217,119,6,0.08)', color: '#b45309', border: '1px solid rgba(217,119,6,0.2)' }}>
                      {load?.readableOfferId || load?.offerId}
                    </span>
                    {highlightedLoadId === load?.id && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap" style={{ background: 'rgba(217,119,6,0.12)', color: '#b45309', border: '1px solid rgba(217,119,6,0.3)' }}>
                        <Icon name="Zap" size={9} />
                        {t('marketplace.selectedFromMandi', 'Selected from SugarHathi Mandi')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xl font-extrabold" style={{ color: '#b45309' }}>{formatCurrency(load?.rate)}</p>
                  <p className="text-xs mt-0.5" style={{ color: '#64748b' }}>per ton + GST</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold" style={{ color: '#1e293b' }}>{load?.millName}</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap" style={{ background: 'rgba(22,163,74,0.1)', color: '#16a34a', border: '1px solid rgba(22,163,74,0.3)' }}>
                  <Icon name="BadgeCheck" size={10} />Verified
                </span>
              </div>
              <div className="flex items-center gap-1 mt-1">
                <Icon name="MapPin" size={11} className="text-slate-400" />
                <span className="text-xs" style={{ color: '#64748b' }}>{load?.city}, {load?.stateName}</span>
              </div>
            </div>

            {/* Card Body */}
            <div className="px-5 py-4">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: '#94a3b8' }}>Quantity</p>
                  <p className="text-sm font-bold" style={{ color: '#1e293b' }}>{load?.quantity} MT</p>
                  <p className="text-xs mt-0.5" style={{ color: '#64748b' }}>{load?.packSize}</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: '#94a3b8' }}>Distance</p>
                  <p className="text-sm font-bold" style={{ color: '#1e293b' }}>{load?.distanceFromBuyer} km</p>
                  <p className="text-xs mt-0.5" style={{ color: '#64748b' }}>from your location</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: '#94a3b8' }}>Payment Date</p>
                  <p className="text-sm font-medium" style={{ color: '#1e293b' }}>{formatDate(load?.paymentDate)}</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: '#94a3b8' }}>Lifting Date</p>
                  <p className="text-sm font-medium" style={{ color: '#1e293b' }}>{formatDate(load?.liftingDate)}</p>
                </div>
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-2 mb-4">
                {load?.sellerName ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap" style={{ background: 'rgba(22,163,74,0.12)', color: '#15803d', border: '1px solid rgba(22,163,74,0.35)' }}>
                    <Icon name="BadgeCheck" size={11} />Verified Trader Offer
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap" style={{ background: 'rgba(8,145,178,0.1)', color: '#0e7490', border: '1px solid rgba(8,145,178,0.3)' }}>
                    <Icon name="Shield" size={11} />Verified Sugar Mill
                  </span>
                )}
                {load?.trucksAvailable > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap" style={{ background: 'rgba(37,99,235,0.08)', color: '#1d4ed8', border: '1px solid rgba(37,99,235,0.25)' }}>
                    <Icon name="Truck" size={11} />{load?.trucksAvailable} trucks ready
                  </span>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-2.5">
                {(!user || !user?.id || !['buyer', 'admin'].includes(userProfile?.role)) ? null : (
                  <>
                    <button
                      onClick={() => handleBuySugar(load)}
                      className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-extrabold transition-all duration-150"
                      style={{ background: '#d97706', color: '#ffffff', boxShadow: '0 4px 12px rgba(217,119,6,0.35)' }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#b45309'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#d97706'; }}
                    >
                      <Icon name="ShoppingCart" size={16} />
                      Buy Sugar
                    </button>
                    <button
                      onClick={() => handleBookSugarTruck(load)}
                      className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold transition-all duration-150"
                      style={{ background: '#92400e', color: '#ffffff', boxShadow: '0 4px 12px rgba(146,64,14,0.3)' }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#78350f'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#92400e'; }}
                    >
                      <Icon name="Truck" size={15} />
                      Book Sugar + Truck
                    </button>
                  </>
                )}
                <button
                  onClick={() => handleEnquire(load)}
                  className="w-full flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-150"
                  style={{ background: 'transparent', color: '#64748b', border: '1px solid rgba(0,0,0,0.15)' }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#1e293b'; e.currentTarget.style.borderColor = 'rgba(0,0,0,0.3)'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#64748b'; e.currentTarget.style.borderColor = 'rgba(0,0,0,0.15)'; }}
                >
                  <Icon name="Info" size={14} />
                  More Details
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {isDetailsModalOpen && selectedLoad && (
        <LoadDetailsModal
          load={selectedLoad}
          onClose={() => { setIsDetailsModalOpen(false); setSelectedLoad(null); }}
          onBookSugarOnly={onBookSugarOnly}
          onBookBundle={onBookBundle}
        />
      )}
      {isBuySugarModalOpen && selectedLoad && (
        <BuySugarModal
          load={selectedLoad}
          onClose={() => setIsBuySugarModalOpen(false)}
          onConfirm={(purchaseData) => { onBookSugarOnly(purchaseData || selectedLoad); setIsBuySugarModalOpen(false); }}
        />
      )}
      {isMoreDetailsModalOpen && selectedLoad && (
        <MoreDetailsModal
          load={selectedLoad}
          onClose={() => { setIsMoreDetailsModalOpen(false); setSelectedLoad(null); }}
          onBookSugarOnly={onBookSugarOnly}
          onBookBundle={onBookBundle}
        />
      )}
      {sugarTruckModal && (
        <SugarTruckMultiModal
          load={sugarTruckModal}
          selectedQuantity={sugarTruckModal?.selectedQuantity}
          onClose={() => setSugarTruckModal(null)}
          onBackToQuantity={() => {
            const loadData = { ...sugarTruckModal };
            setSugarTruckModal(null);
            setSugarTruckQuantityLoad(loadData);
          }}
        />
      )}
      {sugarTruckQuantityLoad && (
        <BuySugarModal
          load={sugarTruckQuantityLoad}
          onClose={() => setSugarTruckQuantityLoad(null)}
          onConfirm={handleSugarTruckQuantityConfirm}
          forTruck={true}
        />
      )}
      {showLoginModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="rounded-2xl shadow-2xl w-full max-w-sm p-7 text-center" style={{ backgroundColor: '#ffffff' }}>
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#fef3c7' }}>
              <Icon name="LogIn" size={26} color="#d97706" />
            </div>
            <h3 className="text-lg font-bold mb-2" style={{ color: '#1f2937' }}>Login Required</h3>
            <p className="text-sm mb-5" style={{ color: '#6b7280' }}>Please login or register to place your order.</p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowLoginModal(false);
                  const next = encodeURIComponent(window.location.pathname + window.location.search);
                  navigate(`/login-authentication?next=${next}`);
                }}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold"
                style={{ backgroundColor: '#d97706', color: '#ffffff' }}
              >
                Login
              </button>
              <button
                onClick={() => {
                  setShowLoginModal(false);
                  const next = encodeURIComponent(window.location.pathname + window.location.search);
                  navigate(`/user-registration?next=${next}`);
                }}
                className="flex-1 py-2.5 rounded-xl border text-sm font-bold"
                style={{ borderColor: '#d97706', color: '#d97706', backgroundColor: '#fffbeb' }}
              >
                Register
              </button>
            </div>
            <button onClick={() => setShowLoginModal(false)} className="mt-4 text-xs underline" style={{ color: '#9ca3af' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default MarketplaceCards;